import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Check,
  ChevronRight,
  Info,
  AlertTriangle,
  Save,
  Sparkles,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatDateDisplay } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
// Shared premium-data loader (rates + contributions incl. buy-up base plans)
import { usePlanPremiumData } from "@/hooks/usePlanPremiumData";
import { resolveNeedsConfigReason } from "./needsConfig";
import { isCurrentOrUpcomingDependentEnrollmentForPlan, computeEnrollmentPremiumResult } from "@/lib/enrollmentFilters";
import { determineCoverageTierFromDependents } from "@/lib/enrollmentFilters";
import { resolveEnrollmentEffectiveDate } from "@shared/lib/ageDetermination";
import { isLifeDisabilityType, requiresBeneficiary } from "@shared/schemas/plans";
import { isFsaPlanType } from "@shared/schemas/planTypes";
import { getPlanTypeMeta } from "@shared/schemas/planTypes";
import { eligibleDependentsForPlan, planAllowsAnyDependents } from "@shared/utils/dependentEligibility";
import {
  BeneficiaryAllocation,
  type BeneficiaryAllocationValue,
  defaultBeneficiaryAllocation,
  fromBeneficiaryRows,
  isBeneficiaryAllocationValid,
  mirrorPrimaryToLegacy,
  saveBeneficiaries,
} from "@/components/benefits/BeneficiaryAllocation";
import { QLE_EVENT_RULES, calculateEffectiveDateFromQLE } from "@shared/utils/qleTypes";
import { WAIVE_REASONS, LEGACY_TO_NEW, getWaiveReasonLabel, classifyDepReason } from "@shared/utils/waiveReasons";
import { formatBenefitAmount } from "@shared/utils/enrollmentCalculator";
import {
  hasNonMonthlyPayCycle,
  calculatePerPaycheckAmount,
  getPayCycleLabel,
} from "@/lib/payrollUtils";
import { getTypeIcon, isPlanCurrentlyAvailable } from "./typeIcons";
import { SectionHeader, DependentsBody, WaiveReasonPicker } from "./modifySteps";
import { computeFsaPreview, isFsaConfigured, validateFsaElection as validateFsaElectionInput, FSA_NO_PAY_SCHEDULE_ADMIN } from "@/lib/fsaPreview";
import { usePaySchedule } from "@/hooks/usePaySchedule";
import { isPayScheduleAnchored } from "@shared/utils/fsaProration";
import { NeedsConfigPill } from "./NeedsConfigPill";
import { ratingAreaGapPriceMessage } from "@/lib/planPriceDisplay";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatUSD(n: number | null | undefined) {
  const v = typeof n === "number" ? n : parseFloat(String(n ?? 0));
  if (!Number.isFinite(v)) return "$0.00";
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function isFutureDate(iso: string): boolean {
  if (!iso) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const eff = new Date(iso + "T00:00:00"); eff.setHours(0, 0, 0, 0);
  if (Number.isNaN(eff.getTime())) return false;
  return eff > today;
}

const ADMIN_CORRECTION = "administrative_correction";

// Normalize legacy waiver-reason values to the current vocabulary so the
// modal's radio list reflects the saved reason when re-opening an old row.
function normalizeWaiveReason(raw: string | null | undefined): string {
  if (!raw) return WAIVE_REASONS[0].value;
  return LEGACY_TO_NEW[raw] ?? raw;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ModifyTypeModalProps {
  open: boolean;
  onClose: () => void;
  employee: any;
  dependents: any[];
  /** All enrollment rows for this employee (base + dep + waived). */
  employeeBenefits: any[];
  /** Normalized plan type id (e.g., "medical", "dental"). */
  type: string;
  /** Currently-active base enrollment for this type, if any. */
  currentEnrollment?: any | null;
  /** Currently-waived base enrollment for this type, if any. */
  waivedEnrollment?: any | null;
  /** Every clientPlan available to this employee for this type (and any aliases). Format: [{ plan: {...} }, ...] */
  availablePlans: any[];
  onSaved: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────────────────────────

export function ModifyTypeModal(props: ModifyTypeModalProps) {
  const {
    open,
    onClose,
    employee,
    dependents,
    employeeBenefits,
    type,
    currentEnrollment,
    waivedEnrollment,
    availablePlans,
    onSaved,
  } = props;

  const meta = getPlanTypeMeta(type);
  const Icon = getTypeIcon(type);
  const isLD = isLifeDisabilityType(type);
  const isFsa = isFsaPlanType(type);
  const { toast } = useToast();

  // FSA needs a payroll schedule that yields an EXACT deduction (no estimate).
  const { resolvePaySchedule } = usePaySchedule(employee?.clientId);
  const payScheduleReady = isPayScheduleAnchored(resolvePaySchedule(employee?.payGroupId));

  const currentPlanId: number | undefined =
    currentEnrollment?.planId ?? currentEnrollment?.plan?.id ?? undefined;

  // Selected plan id in the Switch plan tab. "__waive__" is a synthetic id.
  const [selectedPlanId, setSelectedPlanId] = useState<number | "__waive__" | "">(
    waivedEnrollment ? "__waive__" : currentPlanId ?? "",
  );

  // Dependent decisions: tri-state per dep ("enroll" | "waive" | undefined=undecided).
  // Initial state seeds:
  //   - dep IDs with active rows on currentEnrollment.planId → "enroll"
  //   - dep IDs present in currentEnrollment.dependentWaiverReasons → "waive"
  //   - everything else → undefined
  const initialDepIds = useMemo(() => {
    if (!currentEnrollment?.planId) return [] as number[];
    return (employeeBenefits || [])
      .filter((b: any) => isCurrentOrUpcomingDependentEnrollmentForPlan(b, currentEnrollment.planId))
      .map((b: any) => Number(b.dependentId));
  }, [employeeBenefits, currentEnrollment?.planId]);
  const initialWaivedDepIds = useMemo(() => {
    const map = (currentEnrollment as any)?.dependentWaiverReasons ?? {};
    if (!map || typeof map !== "object") return [] as number[];
    return Object.keys(map).map((k) => Number(k)).filter((n) => Number.isFinite(n));
  }, [currentEnrollment]);
  // Per-dep waive reasons, seeded from the JSONB. classifyDepReason splits each
  // stored string into a known slug or "other" + freeform text.
  const initialDepReasonsSeed = useMemo(() => {
    const map = (currentEnrollment as any)?.dependentWaiverReasons ?? {};
    const reasons: Record<number, string> = {};
    const otherTexts: Record<number, string> = {};
    if (map && typeof map === "object") {
      for (const [k, raw] of Object.entries(map)) {
        const id = Number(k);
        if (!Number.isFinite(id)) continue;
        const cls = classifyDepReason(raw as string);
        if (cls.reason) reasons[id] = cls.reason;
        if (cls.otherText) otherTexts[id] = cls.otherText;
      }
    }
    return { reasons, otherTexts };
  }, [currentEnrollment]);
  const [depDecisions, setDepDecisions] = useState<Record<number, "enroll" | "waive">>(() => {
    const out: Record<number, "enroll" | "waive"> = {};
    for (const id of initialDepIds) out[id] = "enroll";
    for (const id of initialWaivedDepIds) if (!(id in out)) out[id] = "waive";
    return out;
  });
  const selectedDependents = useMemo(
    () => Object.entries(depDecisions).filter(([, v]) => v === "enroll").map(([k]) => Number(k)),
    [depDecisions],
  );
  const waivedDepIds = useMemo(
    () => Object.entries(depDecisions).filter(([, v]) => v === "waive").map(([k]) => Number(k)),
    [depDecisions],
  );
  const [depWaiveReasons, setDepWaiveReasons] = useState<Record<number, string>>(initialDepReasonsSeed.reasons);
  const [depWaiveOtherTexts, setDepWaiveOtherTexts] = useState<Record<number, string>>(initialDepReasonsSeed.otherTexts);

  // Waive reason
  const [waiveReason, setWaiveReason] = useState<string>(
    normalizeWaiveReason(waivedEnrollment?.eventType),
  );
  const [waiveOtherText, setWaiveOtherText] = useState<string>(
    waivedEnrollment?.eventDescription || "",
  );

  // Reason for change (QLE block)
  const [qleEvent, setQleEvent] = useState<string>(ADMIN_CORRECTION);
  const [qleDate, setQleDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [qleNotes, setQleNotes] = useState<string>("");
  const [qleOpen, setQleOpen] = useState<boolean>(true);
  // Effective date — distinct from the QLE event date. Auto-derived from
  // (qleEvent, qleDate) unless the broker checks "Override calculated date".
  const [effectiveDate, setEffectiveDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [manualEffectiveDate, setManualEffectiveDate] = useState<boolean>(false);

  // Recompute effective date whenever the event type or event date changes,
  // unless the broker has explicitly overridden it.
  useEffect(() => {
    if (!manualEffectiveDate) {
      const next = calculateEffectiveDateFromQLE(qleEvent, qleDate);
      if (next) setEffectiveDate(next);
    }
  }, [qleEvent, qleDate, manualEffectiveDate]);

  // Beneficiaries (life / AD&D only) — multi-beneficiary allocation.
  const [beneficiaries, setBeneficiaries] = useState<BeneficiaryAllocationValue>(
    defaultBeneficiaryAllocation(),
  );

  // FSA annual election (employee-only contribution amount). Seeded from the
  // current enrollment row's annualElection column. Not relevant for non-FSA.
  const [annualElection, setAnnualElection] = useState<string>(
    currentEnrollment?.annualElection != null ? String(currentEnrollment.annualElection) : "",
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state whenever the modal opens with a different context.
  useEffect(() => {
    if (!open) return;
    setSelectedPlanId(waivedEnrollment ? "__waive__" : currentPlanId ?? "");
    const nextDecisions: Record<number, "enroll" | "waive"> = {};
    for (const id of initialDepIds) nextDecisions[id] = "enroll";
    for (const id of initialWaivedDepIds) if (!(id in nextDecisions)) nextDecisions[id] = "waive";
    setDepDecisions(nextDecisions);
    setDepWaiveReasons(initialDepReasonsSeed.reasons);
    setDepWaiveOtherTexts(initialDepReasonsSeed.otherTexts);
    setWaiveReason(normalizeWaiveReason(waivedEnrollment?.eventType));
    setWaiveOtherText(waivedEnrollment?.eventDescription || "");
    setAnnualElection(
      currentEnrollment?.annualElection != null ? String(currentEnrollment.annualElection) : "",
    );
    setQleEvent(ADMIN_CORRECTION);
    const today = new Date().toISOString().split("T")[0];
    setQleDate(today);
    setEffectiveDate(today);
    setManualEffectiveDate(false);
    setQleNotes("");
    setQleOpen(true);
    setBeneficiaries(defaultBeneficiaryAllocation());
    setIsSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentPlanId, waivedEnrollment?.id, currentEnrollment?.id]);

  // Seed beneficiaries from the saved rows for the current enrollment.
  useEffect(() => {
    if (!open || !currentEnrollment?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/enrollments/${currentEnrollment.id}/beneficiaries`);
        if (!r.ok) return;
        const rows = await r.json().catch(() => []);
        if (!cancelled && Array.isArray(rows) && rows.length > 0) {
          setBeneficiaries(fromBeneficiaryRows(rows));
        }
      } catch {
        /* non-blocking: fall back to the default empty primary row */
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentEnrollment?.id]);

  // ── Plan rates / contributions (batched fetch) ─────────────────────────────
  const planIds = useMemo(
    () => (availablePlans || []).map((pa: any) => pa?.plan?.id).filter(Boolean),
    [availablePlans],
  );

  // DEV-262: one shared loader replaces the modal's two hand-rolled queries
  // plus the base-plan follow-up fetch below. Passing [] while the modal is
  // closed keeps the query disabled (the old `enabled: open` behavior).
  // Destructuring keeps the previous names so all consumers are unchanged.
  const {
    ratesByPlanId: planRatesByPlan,
    contribsByPlanId: planContribsByPlan,
    allPlanRates: allPlanRatesFlat,
    allPlanContributions: allPlanContributionsFlat,
    ratingAreaGapsByPlanId,
  } = usePlanPremiumData(open ? planIds : [], employee?.clientId);

  // DEV-382: a plan with rates, but none for the group's rating area, computes
  // to $0. It must read "Can't price", never $0, a delta or a $0 in the total.
  function cantPriceFor(planRecord: any): string | null {
    if (!planRecord) return null;
    return ratingAreaGapPriceMessage(ratingAreaGapsByPlanId?.[planRecord.id], planRecord.name);
  }

  const { data: ldConfigsByPlan = {} } = useQuery<Record<number, any>>({
    queryKey: ["modify-type-modal:ld-configs", planIds, type],
    queryFn: async () => {
      const out: Record<number, any> = {};
      if (!isLD) return out;
      await Promise.all(
        planIds.map(async (pid: number) => {
          try {
            const r = await fetch(`/api/plans/${pid}/life-disability-config`);
            if (r.ok) {
              const d = await r.json();
              if (d) out[pid] = d;
            }
          } catch { /* ignore */ }
        }),
      );
      return out;
    },
    enabled: open && isLD && planIds.length > 0,
    staleTime: Infinity,
  });

  // DEV-262: removed the basePlanIds memo + base-plan follow-up query + the
  // two flattening memos here — the shared loader above already includes
  // referenced buy-up base plans in its keyed and flattened outputs.

  // FSA per-plan config (annual min/max etc.) — only fetched for FSA modals.
  const { data: fsaConfigsByPlan = {}, isFetched: fsaConfigsFetched } = useQuery<Record<number, any>>({
    queryKey: ["modify-type-modal:fsa-configs", planIds, type],
    queryFn: async () => {
      const out: Record<number, any> = {};
      if (!isFsa) return out;
      await Promise.all(
        planIds.map(async (pid: number) => {
          try {
            const r = await fetch(`/api/plans/${pid}/fsa-config`);
            if (r.ok) {
              const d = await r.json();
              if (d) out[pid] = d;
            }
          } catch { /* ignore */ }
        }),
      );
      return out;
    },
    enabled: open && isFsa && planIds.length > 0,
    staleTime: Infinity,
  });

  // ── Premium computation for a given (plan, dependent set) ──────────────────
  // Returns null while the per-plan rates fetch is still pending OR errored;
  // callers should render a loading placeholder rather than $0 (which would
  // mis-classify the row as "Employer paid").
  function premiumFor(planRecord: any, depIds: number[]): { ee: number; warnings: string[]; benefitAmount?: any; benefitAmountPeriod?: any } | null {
    const plan = planRecord;
    if (!plan) return null;
    const pid = plan.id as number;
    // `planRatesByPlan[pid] === undefined` means the fetch hasn't resolved yet.
    // Distinguish from `=== []` (resolved, plan genuinely has no rate rows).
    if (planRatesByPlan[pid] === undefined) return null;
    const selectedDeps = (dependents || []).filter((d: any) => depIds.includes(d.id));
    try {
      const res: any = computeEnrollmentPremiumResult({
        plan,
        planRates: planRatesByPlan[pid] || [],
        planContributions: planContribsByPlan[pid] || [],
        // Base-plan rows so a buy-up plan's employer contribution resolves
        // (otherwise the employee is shown the full premium).
        allPlanContributions: allPlanContributionsFlat,
        allPlanRates: allPlanRatesFlat,
        employee,
        dependents: selectedDeps,
        // Rate as of the current enrollment's effective date (matches the card).
        effectiveDate: currentEnrollment?.effectiveDate ?? undefined,
        ldConfig: ldConfigsByPlan[pid],
      });
      if (!res) return null;
      return {
        ee: Number(res?.employeePremium ?? 0),
        warnings: res?.warnings ?? [],
        benefitAmount: res?.benefitAmount,
        benefitAmountPeriod: res?.benefitAmountPeriod,
      };
    } catch {
      return null;
    }
  }

  // Derived premium for current selection (for footer + delta)
  const currentPlanRecord = useMemo(
    () => (availablePlans || []).find((pa: any) => pa?.plan?.id === currentPlanId)?.plan,
    [availablePlans, currentPlanId],
  );
  const selectedPlanRecord = useMemo(() => {
    if (selectedPlanId === "__waive__" || selectedPlanId === "") return null;
    return (availablePlans || []).find((pa: any) => pa?.plan?.id === selectedPlanId)?.plan;
  }, [availablePlans, selectedPlanId]);

  // The plan the effective date is written against: the newly picked plan on a switch,
  // else the current one. On a switch the OLD plan only receives an endDate, so
  // validating it too would wrongly block a renewal into next year's plan.
  const targetPlanRecord = selectedPlanRecord ?? currentPlanRecord ?? null;

  /**
   * Coverage can't start outside the target plan's own year — that rates the member
   * against the wrong year. Shown even when the date input is disabled, because the
   * auto-calculated QLE date can land out of range without the broker touching it.
   */
  const effectiveDateErrors = useMemo(() => {
    if (!effectiveDate || !targetPlanRecord) return [] as string[];
    const { tooEarly, tooLate, planStart, planEnd } = resolveEnrollmentEffectiveDate(
      effectiveDate,
      targetPlanRecord,
    );
    const errors: string[] = [];
    if (tooEarly || tooLate) {
      const range = planEnd ? `${planStart} to ${planEnd}` : `${planStart} or later`;
      errors.push(`${(targetPlanRecord as any).name} covers ${range} — pick a date in that range.`);
    }
    // Waiting period (earliestEffectiveDate from withPlanEligibility; absent
    // without a hire date or on pages that don't overlay eligibility).
    const earliest = (targetPlanRecord as any).earliestEffectiveDate as string | null | undefined;
    if (selectedPlanId !== "__waive__" && earliest && effectiveDate < earliest) {
      errors.push(`Coverage can't start before ${formatDateDisplay(earliest)}, when the waiting period ends.`);
    }
    return errors;
  }, [effectiveDate, targetPlanRecord, selectedPlanId]);

  // Move the auto-calculated date to the end of the waiting period; a manual date gets the error above.
  const earliestEffectiveDate: string | null =
    selectedPlanId === "__waive__" ? null : (targetPlanRecord as any)?.earliestEffectiveDate ?? null;
  useEffect(() => {
    if (!manualEffectiveDate && earliestEffectiveDate && effectiveDate < earliestEffectiveDate) {
      setEffectiveDate(earliestEffectiveDate);
    }
  }, [earliestEffectiveDate, effectiveDate, manualEffectiveDate]);

  // null = no current plan, or it can't be priced (DEV-382).
  const currentPremium = useMemo(() => {
    if (!currentPlanRecord || cantPriceFor(currentPlanRecord)) return null;
    return premiumFor(currentPlanRecord, initialDepIds)?.ee ?? 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlanRecord, initialDepIds, planRatesByPlan, planContribsByPlan, ldConfigsByPlan, allPlanRatesFlat, allPlanContributionsFlat, ratingAreaGapsByPlanId]);

  // Footer "Can't price" message for the plan the footer is pricing.
  const newPlanCantPrice =
    selectedPlanId === "__waive__" ? null : cantPriceFor(selectedPlanRecord ?? currentPlanRecord);

  const newPremium = useMemo(() => {
    if (selectedPlanId === "__waive__") return 0;
    if (!selectedPlanRecord) return currentPremium ?? 0;
    return premiumFor(selectedPlanRecord, selectedDependents)?.ee ?? 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanRecord, selectedDependents, selectedPlanId, currentPremium, planRatesByPlan, planContribsByPlan, ldConfigsByPlan, allPlanRatesFlat, allPlanContributionsFlat]);

  const isPlanChange =
    selectedPlanRecord != null && currentPlanId != null && selectedPlanRecord.id !== currentPlanId;
  const isWaiveAction = selectedPlanId === "__waive__";

  // ── Coverage-tier compatibility check (defensive, rates-driven) ────────────
  const tierWarning: string | null = useMemo(() => {
    if (!selectedPlanRecord || isWaiveAction) return null;
    const lifeDisab = isLifeDisabilityType(String(selectedPlanRecord.type || ""));
    if (lifeDisab) return null; // volume-rated; tier check doesn't apply the same way
    const tier = determineCoverageTierFromDependents(dependents || [], selectedDependents);
    const rates = planRatesByPlan[selectedPlanRecord.id] || [];
    if (rates.length === 0) return null; // no rates loaded; can't judge
    const offeredTiers = new Set(
      rates.map((r: any) => String(r.coverageTier || "").toLowerCase()).filter(Boolean),
    );
    if (offeredTiers.size === 0) return null;
    if (!offeredTiers.has(String(tier).toLowerCase())) {
      return `This plan doesn't support the "${String(tier).replace(/_/g, " ")}" tier with the selected dependents.`;
    }
    return null;
  }, [selectedPlanRecord, selectedDependents, dependents, planRatesByPlan, isWaiveAction]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  // Per-save dispatch trace. Reset at the top of handleSubmit; populated by
  // patch/post helpers; printed as a console.table at the end (DEV only).
  const saveTraceRef = React.useRef<
    Array<{ op: "PATCH" | "POST"; id: number | null; planId?: number; depId?: number | null; status?: string; ok: boolean; error?: string }>
  >([]);

  async function patchEnrollment(id: number, body: any) {
    if (import.meta.env.DEV) {
      console.log(`[ModifyTypeModal] PATCH /api/enrollments/${id}`, body);
    }
    try {
      const r = await fetch(`/api/enrollments/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const msg = err.message || err.error || "Failed to update enrollment";
        saveTraceRef.current.push({ op: "PATCH", id, planId: body?.planId, depId: body?.dependentId ?? null, status: body?.status, ok: false, error: msg });
        throw new Error(msg);
      }
      saveTraceRef.current.push({ op: "PATCH", id, planId: body?.planId, depId: body?.dependentId ?? null, status: body?.status, ok: true });
      return r.json().catch(() => ({}));
    } catch (e: any) {
      if (!saveTraceRef.current.some((t) => t.op === "PATCH" && t.id === id && t.ok === false)) {
        saveTraceRef.current.push({ op: "PATCH", id, planId: body?.planId, depId: body?.dependentId ?? null, status: body?.status, ok: false, error: e?.message });
      }
      throw e;
    }
  }

  async function postEnrollment(body: any) {
    if (import.meta.env.DEV) {
      console.log(`[ModifyTypeModal] POST /api/enrollments`, body);
    }
    try {
      const r = await fetch("/api/enrollments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const msg = err.message || err.error || "Failed to create enrollment";
        saveTraceRef.current.push({ op: "POST", id: null, planId: body?.planId, depId: body?.dependentId ?? null, status: body?.status, ok: false, error: msg });
        throw new Error(msg);
      }
      const json = await r.json().catch(() => ({}));
      saveTraceRef.current.push({ op: "POST", id: json?.id ?? null, planId: body?.planId, depId: body?.dependentId ?? null, status: body?.status, ok: true });
      return json;
    } catch (e: any) {
      if (!saveTraceRef.current.some((t) => t.op === "POST" && t.planId === body?.planId && t.depId === (body?.dependentId ?? null) && t.ok === false)) {
        saveTraceRef.current.push({ op: "POST", id: null, planId: body?.planId, depId: body?.dependentId ?? null, status: body?.status, ok: false, error: e?.message });
      }
      throw e;
    }
  }

  async function terminateDependentRowsForPlan(planId: number, endDate: string) {
    // Terminate ANY non-terminated dep row for this plan, including legacy
    // rows in a corrupt (status=active, waivedCoverage=true) state. The
    // strict-active filter would leak those.
    const depRows = (employeeBenefits || []).filter(
      (b: any) =>
        b?.planId === planId &&
        Number.isFinite(b?.dependentId) &&
        String(b?.status ?? "").toLowerCase() !== "terminated",
    );
    for (const dep of depRows) {
      if (dep?.id) {
        await patchEnrollment(dep.id, { status: "terminated", endDate });
      }
    }
  }

  /**
   * Reconcile a desired dep enrollment against ALL pre-existing rows for
   * (planId, depId). Handles multi-row corruption: keeps/reactivates one
   * canonical row, terminates orphans, never posts a fresh row when any
   * already exists. Defends against client-server dedup races AND legacy
   * duplicate rows.
   */
  async function reconcileDepEnrollment(
    planId: number,
    depId: number,
    payload: Record<string, any>,
  ) {
    const matches = (employeeBenefits || []).filter(
      (b: any) => b?.planId === planId && Number(b?.dependentId) === depId,
    );
    if (matches.length === 0) {
      return postEnrollment({ ...payload, planId, dependentId: depId });
    }

    // Keeper precedence: an already-active row (if any) wins; otherwise the
    // most-recently-modified row gets reactivated.
    const active = matches.find(
      (m: any) => String(m.status).toLowerCase() === "active" && !m.waivedCoverage,
    );
    const sortedByRecency = [...matches].sort((a, b) => {
      const at = new Date(a.lastModified ?? a.effectiveDate ?? 0).getTime();
      const bt = new Date(b.lastModified ?? b.effectiveDate ?? 0).getTime();
      return bt - at;
    });
    const keeper = active ?? sortedByRecency[0];

    // Terminate every other non-terminated row so we collapse to exactly one.
    for (const m of matches) {
      if (m.id !== keeper.id && String(m.status ?? "").toLowerCase() !== "terminated") {
        await patchEnrollment(m.id, { status: "terminated", endDate: payload.effectiveDate });
      }
    }

    if (active) {
      if (import.meta.env.DEV) {
        console.warn("[ModifyTypeModal] dep already actively enrolled — skipping POST", {
          existingId: active.id, planId, depId,
        });
      }
      return active;
    }

    // No active keeper — PATCH the chosen row back to life.
    return patchEnrollment(keeper.id, {
      ...payload,
      planId,
      dependentId: depId,
      endDate: null,
      waivedCoverage: false,
    });
  }

  function validate(): string | null {
    if (!qleEvent) return "Please select a reason for this change.";
    if (!qleDate) return "Please enter the event date.";
    if (!effectiveDate) return "Please enter an effective date.";
    if (effectiveDateErrors.length) return effectiveDateErrors.join(" ");
    if (isWaiveAction && !waiveReason) return "Please pick a waiver reason.";
    if (isWaiveAction && waiveReason === "other" && !waiveOtherText.trim())
      return "Please describe the waiver reason.";
    if (!isWaiveAction && !selectedPlanRecord && !currentPlanId)
      return "Please pick a plan or choose to waive.";
    if (requiresBeneficiary((selectedPlanRecord ?? currentPlanRecord)?.type) && !isWaiveAction && !isBeneficiaryAllocationValid(beneficiaries))
      return "Each beneficiary needs a name + relationship, and primary (and contingent, if any) must total 100%.";
    if (!isWaiveAction && !isFsa) {
      for (const id of waivedDepIds) {
        const r = depWaiveReasons[id];
        if (!r) return "Please pick a reason for each waived dependent.";
        if (r === "other" && !(depWaiveOtherTexts[id] || "").trim())
          return "Please describe why each waived dependent is being waived.";
      }
    }
    if (!isWaiveAction && isFsa) {
      const planRecord = selectedPlanRecord ?? currentPlanRecord;
      const cfg = planRecord ? fsaConfigsByPlan[Number(planRecord.id)] : null;
      const result = validateFsaElectionInput(annualElection, cfg, employee?.payCycle, planRecord?.name, { payScheduleReady });
      if (!result.ok) {
        // Admin-facing: point the broker at the config / payroll setup.
        if (result.code === "not_configured")
          return "This FSA plan isn't configured. Set the annual election limits in the plan's FSA Config tab before enrolling.";
        if (result.code === "no_pay_schedule") return FSA_NO_PAY_SCHEDULE_ADMIN;
        return result.message ?? "Invalid FSA election.";
      }
    }
    if (tierWarning && !isFsa) return tierWarning;
    return null;
  }

  async function handleSubmit() {
    // Guard against double-click before React flushes setIsSubmitting(true).
    if (isSubmitting) return;
    const err = validate();
    if (err) {
      toast({ title: err, variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    saveTraceRef.current = [];
    if (import.meta.env.DEV) {
      console.group(
        `[ModifyTypeModal] save · ${type} · ${employee?.firstName ?? ""} ${employee?.lastName ?? ""}`,
      );
    }
    try {
      const eventFields = {
        eventType: isWaiveAction
          ? (waiveReason === "other" ? "other" : waiveReason)
          : qleEvent,
        eventDate: qleDate,
        eventDescription:
          isWaiveAction && waiveReason === "other"
            ? waiveOtherText.trim() || null
            : qleNotes.trim() || null,
      };
      // Future effective dates land as "pending" so the activation cron picks
      // them up and they render in the Upcoming Benefits section until the
      // start date arrives. Mirrors BenefitsConfigurationForm's logic.
      const computedStatus: "pending" | "active" = isFutureDate(effectiveDate) ? "pending" : "active";

      // Target plan being saved (selected on plan-switch, or the current plan
      // on a same-plan edit). FSA detection happens off this record so the
      // submit applies the IRS employee-only rule regardless of which branch
      // we land in.
      const targetIsFsa = isFsaPlanType(String(targetPlanRecord?.type || ""));
      // Decimal column → send as a string (server Zod rejects a number).
      const annualElectionValue =
        targetIsFsa && annualElection.trim() ? String(Number(annualElection)) : null;

      // Compose per-dependent waiver JSONB (applied to the base/employee row).
      // Each waived dep carries its own reason — "other" stores the free text
      // directly as the value (display path normalizes via getWaiveReasonLabel).
      // When the whole plan is being waived OR the target is FSA (which has
      // no dep concept), per-dep granularity is moot.
      const dependentWaiverReasons: Record<string, string> | null =
        !isWaiveAction && !targetIsFsa && waivedDepIds.length > 0
          ? Object.fromEntries(
              waivedDepIds.map((id) => {
                const r = depWaiveReasons[id];
                const resolved = r === "other"
                  ? (depWaiveOtherTexts[id] || "").trim()
                  : r;
                return [String(id), resolved];
              }),
            )
          : null;

      // Beneficiaries (life / AD&D). Mirror primary[0] into the legacy enrollment
      // columns for back-compat; the full set is persisted via /replace below.
      const wantsBeneficiaries = !isWaiveAction && requiresBeneficiary(String(targetPlanRecord?.type || ""));
      const legacy = wantsBeneficiaries
        ? mirrorPrimaryToLegacy(beneficiaries)
        : { beneficiaryName: null, beneficiaryRelationship: null };
      // Base (employee) enrollment id, captured per branch, used to persist beneficiaries.
      let baseEnrollmentId: number | null = null;

      // ─── Waive action ───────────────────────────────────────────────────
      if (isWaiveAction) {
        if (currentEnrollment?.id) {
          await patchEnrollment(currentEnrollment.id, {
            coverageTier: "employee_only",
            status: "waived",
            waivedCoverage: true,
            effectiveDate,
            ...eventFields,
          });
          await terminateDependentRowsForPlan(currentEnrollment.planId, effectiveDate);
        } else if (waivedEnrollment?.id) {
          await patchEnrollment(waivedEnrollment.id, {
            ...eventFields,
          });
        } else {
          // No existing enrollment of any kind — POST a fresh waived row using
          // the first available plan of this type (the backend stores waivers
          // against a specific plan id).
          const planRecord = (availablePlans || [])[0]?.plan;
          if (!planRecord) throw new Error("No plan available to waive against.");
          await postEnrollment({
            clientId: employee.clientId,
            employeeId: employee.id,
            planId: planRecord.id,
            coverageTier: "employee_only",
            status: "waived",
            enrollmentSource: "broker_manual",
            effectiveDate,
            waivedCoverage: true,
            ...eventFields,
          });
        }
      }
      // ─── Plan switch (different plan than current) ──────────────────────
      else if (isPlanChange && selectedPlanRecord) {
        // 1. Terminate current base + dep rows
        if (currentEnrollment?.id) {
          await patchEnrollment(currentEnrollment.id, {
            status: "terminated",
            endDate: effectiveDate,
            ...eventFields,
          });
          await terminateDependentRowsForPlan(currentEnrollment.planId, effectiveDate);
        }
        if (waivedEnrollment?.id) {
          await patchEnrollment(waivedEnrollment.id, {
            status: "terminated",
            endDate: effectiveDate,
          });
        }
        // 2. Create the new enrollment with dependents bundled.
        const coverageTier = targetIsFsa
          ? "employee_only"
          : determineCoverageTierFromDependents(dependents || [], selectedDependents);
        const created = await postEnrollment({
          clientId: employee.clientId,
          employeeId: employee.id,
          planId: selectedPlanRecord.id,
          coverageTier,
          status: computedStatus,
          enrollmentSource: "broker_manual",
          effectiveDate,
          waivedCoverage: false,
          beneficiaryName: legacy.beneficiaryName,
          beneficiaryRelationship: legacy.beneficiaryRelationship,
          selectedDependentIds: targetIsFsa ? [] : selectedDependents,
          dependentWaiverReasons,
          ...(annualElectionValue != null ? { annualElection: annualElectionValue } : {}),
          ...eventFields,
        });
        baseEnrollmentId = created?.id ?? null;
      }
      // ─── Same plan, dependent / beneficiary / FSA edit only ─────────────
      else if (currentEnrollment?.id) {
        const coverageTier = targetIsFsa
          ? "employee_only"
          : determineCoverageTierFromDependents(dependents || [], selectedDependents);
        // Compute dep diff up-front. For FSA both sets are empty so depSetChanged
        // is false (and the QLE attaches to the base row, which is the right
        // place for an FSA election change anyway).
        const effectiveSelected = targetIsFsa ? [] : selectedDependents;
        const selectedSet = new Set(effectiveSelected);
        const currentSet = new Set(initialDepIds);
        const toRemove = targetIsFsa
          ? (initialDepIds as number[])  // terminate any pre-existing dep rows on an FSA plan
          : initialDepIds.filter((id) => !selectedSet.has(id));
        const toAdd = targetIsFsa ? [] : effectiveSelected.filter((id) => !currentSet.has(id));
        const depSetChanged = toRemove.length > 0 || toAdd.length > 0;
        await patchEnrollment(currentEnrollment.id, {
          coverageTier,
          status: computedStatus,
          waivedCoverage: false,
          effectiveDate,
          beneficiaryName: wantsBeneficiaries ? legacy.beneficiaryName : undefined,
          beneficiaryRelationship: wantsBeneficiaries ? legacy.beneficiaryRelationship : undefined,
          dependentWaiverReasons,
          ...(annualElectionValue != null ? { annualElection: annualElectionValue } : {}),
          ...(depSetChanged ? {} : eventFields),
        });
        baseEnrollmentId = currentEnrollment.id;
        for (const depId of toRemove) {
          // Terminate EVERY non-terminated row for this dep, not just the
          // first match. Defends against legacy duplicate rows (one terminated,
          // one still active) where .find() would have only handled one.
          const depEnrs = (employeeBenefits || []).filter(
            (b: any) =>
              b?.planId === currentEnrollment.planId &&
              Number(b?.dependentId) === depId &&
              String(b?.status ?? "").toLowerCase() !== "terminated",
          );
          for (const depEnr of depEnrs) {
            if (depEnr?.id) {
              await patchEnrollment(depEnr.id, { status: "terminated", endDate: effectiveDate });
            }
          }
        }
        for (const depId of toAdd) {
          await reconcileDepEnrollment(currentEnrollment.planId, depId, {
            clientId: employee.clientId,
            employeeId: employee.id,
            coverageTier,
            status: computedStatus,
            enrollmentSource: "broker_manual",
            effectiveDate,
            ...eventFields,
          });
        }
      }
      // ─── No current enrollment but user picked a plan (came from empty row) ─
      else if (selectedPlanRecord) {
        const coverageTier = targetIsFsa
          ? "employee_only"
          : determineCoverageTierFromDependents(dependents || [], selectedDependents);
        if (waivedEnrollment?.id) {
          await patchEnrollment(waivedEnrollment.id, {
            status: "terminated",
            endDate: effectiveDate,
          });
        }
        const created = await postEnrollment({
          clientId: employee.clientId,
          employeeId: employee.id,
          planId: selectedPlanRecord.id,
          coverageTier,
          status: computedStatus,
          enrollmentSource: "broker_manual",
          effectiveDate,
          waivedCoverage: false,
          beneficiaryName: legacy.beneficiaryName,
          beneficiaryRelationship: legacy.beneficiaryRelationship,
          selectedDependentIds: targetIsFsa ? [] : selectedDependents,
          dependentWaiverReasons,
          ...(annualElectionValue != null ? { annualElection: annualElectionValue } : {}),
          ...eventFields,
        });
        baseEnrollmentId = created?.id ?? null;
      }

      // Persist the full beneficiary set against the base enrollment row.
      if (wantsBeneficiaries && baseEnrollmentId && targetPlanRecord) {
        await saveBeneficiaries(baseEnrollmentId, beneficiaries, {
          employeeId: employee.id,
          planId: Number(targetPlanRecord.id),
        });
      }

      toast({ title: "Benefit updated", variant: "success" });
      onSaved();
      onClose();
    } catch (e: any) {
      console.error("ModifyTypeModal submit error:", e);
      toast({ title: "Failed to update benefit", description: e?.message, variant: "destructive" });
    } finally {
      if (import.meta.env.DEV) {
        if (saveTraceRef.current.length > 0) {
          console.table(saveTraceRef.current);
        }
        console.groupEnd();
      }
      setIsSubmitting(false);
    }
  }

  // ── Render helpers ─────────────────────────────────────────────────────────
  const isNonMonthly = hasNonMonthlyPayCycle(employee?.payCycle);
  const cycleLabel = isNonMonthly ? `per ${getPayCycleLabel(employee?.payCycle)}` : "per month";
  const oldPerCycle = isNonMonthly
    ? calculatePerPaycheckAmount(currentPremium ?? 0, employee?.payCycle)
    : currentPremium ?? 0;
  const newPerCycle = isNonMonthly
    ? calculatePerPaycheckAmount(newPremium, employee?.payCycle)
    : newPremium;
  const delta = newPerCycle - oldPerCycle;
  // No old→new delta against a side that can't be priced (DEV-382).
  const showDelta =
    (isPlanChange || isWaiveAction) && currentPremium != null && !newPlanCantPrice && Math.abs(delta) > 0.005;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden p-0 flex flex-col">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center">
              <Icon className="h-5 w-5 text-stone-600 dark:text-stone-400" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-[15px] font-semibold tracking-tight">
                Modify {meta.label.toLowerCase()} · {employee?.firstName} {employee?.lastName}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {currentEnrollment?.plan?.name
                  ? `Currently on ${currentEnrollment.plan.name}`
                  : waivedEnrollment
                    ? "Currently waived"
                    : "No election yet"}
                {currentEnrollment?.effectiveDate && (
                  <span className="text-stone-400"> · eff. {formatDateDisplay(currentEnrollment.effectiveDate)}</span>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Body — linear flow (no tabs). Section 1: plan picker. Section 2: dependents OR plan-level reason. Section 3: QLE. */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0 space-y-6">
            <section className="space-y-3">
              <SectionHeader index={1} title={`Choose a ${meta.label.toLowerCase()} plan or waive`} />
              <SwitchPlanBody
                availablePlans={availablePlans}
                selectedId={selectedPlanId}
                onSelect={setSelectedPlanId}
                currentPlanId={currentPlanId}
                premiumFor={premiumFor}
                cantPriceFor={cantPriceFor}
                currentPremium={currentPremium}
                isNonMonthly={isNonMonthly}
                cycleLabel={cycleLabel}
                payCycle={employee?.payCycle}
                selectedDependents={selectedDependents}
                planContribsByPlan={planContribsByPlan}
                fsaConfigsByPlan={fsaConfigsByPlan}
                fsaConfigsFetched={fsaConfigsFetched}
              />
              {requiresBeneficiary((selectedPlanRecord ?? currentPlanRecord)?.type) && !isWaiveAction && (
                <BeneficiaryAllocation value={beneficiaries} onChange={setBeneficiaries} />
              )}
            </section>

            {selectedPlanId === "__waive__" ? (
              <section className="space-y-3">
                <SectionHeader index={2} title="Reason for waiving" />
                <WaiveReasonPicker
                  reason={waiveReason}
                  onReason={setWaiveReason}
                  otherText={waiveOtherText}
                  onOtherText={setWaiveOtherText}
                />
              </section>
            ) : isFsa && (selectedPlanRecord || currentPlanId) ? (
              <section className="space-y-3">
                <SectionHeader index={2} title="Annual election" />
                <FsaElectionBody
                  annualElection={annualElection}
                  onChange={setAnnualElection}
                  fsaConfig={
                    fsaConfigsByPlan[
                      Number(selectedPlanRecord?.id ?? currentPlanId)
                    ] ?? null
                  }
                  payCycle={employee?.payCycle}
                  payScheduleReady={payScheduleReady}
                />
              </section>
            ) : (selectedPlanRecord || currentPlanId) && !planAllowsAnyDependents(selectedPlanRecord ?? currentPlanRecord) ? (
              <section className="space-y-3">
                <SectionHeader index={2} title="Dependents" />
                <p className="text-sm text-muted-foreground">
                  This plan covers the employee only — dependents aren't eligible.
                </p>
              </section>
            ) : (selectedPlanRecord || currentPlanId) ? (
              <section className="space-y-3">
                <SectionHeader index={2} title="Dependents" />
                <DependentsBody
                  dependents={eligibleDependentsForPlan(selectedPlanRecord ?? currentPlanRecord, dependents)}
                  depDecisions={depDecisions}
                  onSetDecision={(id, decision) =>
                    setDepDecisions((prev) => ({ ...prev, [id]: decision }))
                  }
                  depWaiveReasons={depWaiveReasons}
                  onSetDepWaiveReason={(id, v) =>
                    setDepWaiveReasons((prev) => ({ ...prev, [id]: v }))
                  }
                  depWaiveOtherTexts={depWaiveOtherTexts}
                  onSetDepWaiveOtherText={(id, v) =>
                    setDepWaiveOtherTexts((prev) => ({ ...prev, [id]: v }))
                  }
                />
                {tierWarning && (
                  <div className="rounded-md border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 text-xs px-3 py-2 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    {tierWarning}
                  </div>
                )}
              </section>
            ) : null}

            <section className="space-y-2">
              <SectionHeader index={3} title="Reason for this change" />
              <div className="rounded-md border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/50">
              <button
                type="button"
                onClick={() => setQleOpen((v) => !v)}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-left"
              >
                <ChevronRight
                  className={cn("h-3.5 w-3.5 text-stone-500 transition-transform", qleOpen && "rotate-90")}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-stone-900 dark:text-stone-100">
                    Reason for this change
                  </div>
                  <div className="text-[11.5px] text-stone-500">
                    <span className="font-semibold text-stone-700 dark:text-stone-300">
                      {QLE_EVENT_RULES.find((o) => o.value === qleEvent)?.label}
                    </span>
                    {" · event "}
                    {qleDate || "—"}
                    {effectiveDate && (
                      <>
                        {" · eff. "}
                        <span className="font-mono">{effectiveDate}</span>
                      </>
                    )}
                    {qleNotes && <span className="text-stone-500"> · note attached</span>}
                  </div>
                </div>
                {qleEvent === ADMIN_CORRECTION && (
                  <Badge variant="outline" className="bg-stone-100 text-stone-700 border-stone-300 text-[10px]">
                    No QLE required
                  </Badge>
                )}
              </button>
              {qleOpen && (
                <div className="px-3 pb-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px]">
                        Qualifying life event <span className="text-amber-700">*</span>
                      </Label>
                      <Select value={qleEvent} onValueChange={setQleEvent}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {QLE_EVENT_RULES.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px]">
                        Event date <span className="text-amber-700">*</span>
                      </Label>
                      <Input type="date" value={qleDate} onChange={(e) => setQleDate(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-[11px]">
                        Effective date <span className="text-amber-700">*</span>
                      </Label>
                      <label className="inline-flex items-center gap-1.5 text-[10.5px] text-stone-500 cursor-pointer">
                        <Checkbox
                          className="h-3.5 w-3.5"
                          checked={manualEffectiveDate}
                          onCheckedChange={(c) => setManualEffectiveDate(Boolean(c))}
                        />
                        Override calculated date
                      </label>
                    </div>
                    <Input
                      type="date"
                      value={effectiveDate}
                      disabled={!manualEffectiveDate}
                      onChange={(e) => {
                        setEffectiveDate(e.target.value);
                        setManualEffectiveDate(true);
                      }}
                    />
                    {!manualEffectiveDate && (
                      <div className="text-[10.5px] text-stone-500 mt-1">
                        Auto-calculated from the event type. Check "Override" to edit.
                      </div>
                    )}
                    {effectiveDateErrors.map((message) => (
                      <div key={message} className="text-[10.5px] text-destructive mt-1">
                        {message}
                      </div>
                    ))}
                    {effectiveDateErrors.length === 0 && isFutureDate(effectiveDate) && (
                      <div className="text-[10.5px] text-blue-700 dark:text-blue-300 mt-1 inline-flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Future date — this election will be saved as <b>Pending</b> until it starts.
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-[11px]">Additional notes (optional)</Label>
                    <Textarea
                      value={qleNotes}
                      onChange={(e) => setQleNotes(e.target.value)}
                      placeholder="Provide additional context about this change…"
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>
            </section>
          </div>

          {/* Footer */}
          <DialogFooter className="px-5 py-3 border-t bg-stone-50 dark:bg-stone-900/40 flex flex-row items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-wide font-semibold text-stone-500">
                {isWaiveAction ? "New premium" : "Per paycheck"}
              </div>
              <div className="inline-flex items-baseline gap-2">
                {newPlanCantPrice ? (
                  <span
                    className="text-xs text-amber-700 dark:text-amber-400 inline-flex items-start gap-1"
                    data-testid="modify-footer-price-unavailable"
                    title={newPlanCantPrice}
                  >
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    Can't price this plan — see the plan card for the reason.
                  </span>
                ) : showDelta ? (
                  <>
                    <span className="text-sm font-mono text-stone-500 line-through">
                      {formatUSD(oldPerCycle)}
                    </span>
                    <span className="text-stone-500 text-xs">→</span>
                    <span className="text-[17px] font-bold font-mono tracking-tight text-stone-900 dark:text-stone-100">
                      {formatUSD(newPerCycle)}
                    </span>
                    <span
                      className={cn(
                        "text-[11px] font-mono font-semibold",
                        delta > 0 ? "text-amber-700" : "text-emerald-700",
                      )}
                    >
                      {delta > 0 ? "▲" : "▼"}{formatUSD(Math.abs(delta))}/pp
                    </span>
                  </>
                ) : (
                  <span className="text-[17px] font-bold font-mono tracking-tight text-stone-900 dark:text-stone-100">
                    {formatUSD(newPerCycle)}
                  </span>
                )}
                {!newPlanCantPrice && <span className="text-[10.5px] text-stone-500">{cycleLabel}</span>}
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="gap-1.5">
              {isSubmitting ? "Saving…" : "Save changes"}
              <Save className="h-3.5 w-3.5" />
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab body components
// ─────────────────────────────────────────────────────────────────────────────

function SwitchPlanBody({
  availablePlans,
  selectedId,
  onSelect,
  currentPlanId,
  premiumFor,
  cantPriceFor,
  currentPremium,
  isNonMonthly,
  cycleLabel,
  payCycle,
  selectedDependents,
  planContribsByPlan,
  fsaConfigsByPlan,
  fsaConfigsFetched,
}: {
  availablePlans: any[];
  selectedId: number | "__waive__" | "";
  onSelect: (id: number | "__waive__") => void;
  currentPlanId?: number;
  premiumFor: (plan: any, deps: number[]) => { ee: number; warnings: string[]; benefitAmount?: any; benefitAmountPeriod?: any } | null;
  /** DEV-382: "Can't price: …" for a plan with no rates in the group's rating area. */
  cantPriceFor: (plan: any) => string | null;
  /** Current plan's monthly employee premium; null when there's none or it can't be priced. */
  currentPremium: number | null;
  isNonMonthly: boolean;
  cycleLabel: string;
  payCycle?: string | null;
  selectedDependents: number[];
  /** Per-plan contributions list. `undefined` for a planId means "still fetching"; `[]` means "fetched, no rows". */
  planContribsByPlan: Record<number, any[]>;
  /** Per-plan FSA config (annual limits). Missing planId = no fsa_plan_config row. */
  fsaConfigsByPlan: Record<number, any>;
  /** True once the FSA config query has resolved (avoids flagging plans mid-fetch). */
  fsaConfigsFetched: boolean;
}) {
  // Drop closed plans (endDate in the past) — these shouldn't be selectable
  // for new enrollment / switch-plan flows. Mirrors the wizard filter.
  const plans = (availablePlans || [])
    .map((pa: any) => pa?.plan)
    .filter((p: any) => Boolean(p) && isPlanCurrentlyAvailable(p))
    // Duplicate client_plans links to the same plan would list it twice.
    .filter((p: any, i: number, arr: any[]) => arr.findIndex((x) => x.id === p.id) === i);

  // A required plan locks the choice (no Waive, no other plan), matching the
  // Configure wizard. The current plan stays pickable so tier edits still work.
  const requiredPlan = plans.find((p: any) => !p.ineligibleReason && p.enrollmentRequired);
  const waiveBlocked = Boolean(requiredPlan);

  return (
    <div className="space-y-2">
      <div className="text-[11.5px] text-stone-500">
        {plans.length} plan{plans.length === 1 ? "" : "s"} offered by this client.
      </div>
      {plans.length === 0 && (
        <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-4 text-sm text-stone-600">
          No plans of this type available for this client.
        </div>
      )}
      {plans.map((plan: any) => {
        const isCurrent = plan.id === currentPlanId;
        const isSelected = selectedId === plan.id;
        const lockedByRequired = Boolean(requiredPlan) && plan.id !== requiredPlan.id && !isCurrent;
        const isIneligible = (Boolean(plan.ineligibleReason) || lockedByRequired) && !isCurrent;
        const prem = premiumFor(plan, selectedDependents);
        const isLoading = prem === null;
        const monthly = prem?.ee ?? 0;
        const perCycle = isNonMonthly ? calculatePerPaycheckAmount(monthly, payCycle) : monthly;
        const cantPrice = cantPriceFor(plan);
        // A savings/increase delta needs both sides priced.
        const delta = currentPremium != null && !cantPrice ? monthly - currentPremium : 0;
        const contribsForPlan = planContribsByPlan[plan.id];
        const planIsFsa = isFsaPlanType(String(plan.type || ""));
        // FSA isn't ready until its annual election max is set (no row / 0 = not configured).
        const fsaNeedsConfig =
          planIsFsa && fsaConfigsFetched && !isFsaConfigured(fsaConfigsByPlan[plan.id]);
        const planIsLD = isLifeDisabilityType(String(plan.type || ""));
        const needsConfigReason = resolveNeedsConfigReason({
          loaded: contribsForPlan !== undefined,
          isFsa: planIsFsa,
          isLifeDisability: planIsLD,
          contributionCount: (contribsForPlan || []).length,
          coverageAmountResolved: prem != null ? prem.benefitAmount != null : null,
        });
        return (
          <button
            key={plan.id}
            type="button"
            disabled={isIneligible}
            onClick={() => onSelect(plan.id)}
            className={cn(
              "w-full text-left rounded-lg border bg-white dark:bg-stone-900 px-3.5 py-3 transition-all",
              isIneligible
                ? "border-stone-200 dark:border-stone-700 opacity-60 cursor-not-allowed"
                : isSelected
                ? "border-stone-900 dark:border-stone-100 ring-2 ring-stone-200 dark:ring-stone-700"
                : "border-stone-200 dark:border-stone-700 hover:border-stone-400",
            )}
          >
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  "h-4 w-4 rounded-full border mt-0.5 shrink-0 flex items-center justify-center",
                  isSelected
                    ? "bg-stone-900 dark:bg-stone-100 border-stone-900 dark:border-stone-100"
                    : "border-stone-300 dark:border-stone-600",
                )}
              >
                {isSelected && <Check className="h-2.5 w-2.5 text-white dark:text-stone-900" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13.5px] font-semibold text-stone-900 dark:text-stone-100">
                    {plan.name}
                  </span>
                  {isCurrent && (
                    <Badge variant="outline" className="text-[10px]">Current</Badge>
                  )}
                  {plan.ineligibleReason && (
                    <Badge variant="outline" className="text-[10px] bg-red-50 text-red-800 border-red-200 dark:bg-red-950/30 dark:text-red-200 dark:border-red-800/60">
                      Ineligible
                    </Badge>
                  )}
                  {plan.enrollmentRequired && !plan.ineligibleReason && (
                    <Badge variant="outline" className="text-[10px]">Enrollment required</Badge>
                  )}
                  {plan.recommended && !isCurrent && (
                    <Badge variant="outline" className="text-[10px] gap-1 bg-amber-50 text-amber-800 border-amber-200">
                      <Sparkles className="h-2.5 w-2.5" />
                      Recommended
                    </Badge>
                  )}
                  {fsaNeedsConfig ? (
                    <NeedsConfigPill title='Annual election limits are not set for this FSA plan. Set the maximum election in the plan&apos;s "FSA Config" tab before enrolling.' />
                  ) : needsConfigReason ? (
                    <NeedsConfigPill reason={needsConfigReason} />
                  ) : null}
                </div>
                <div className="text-[11.5px] text-stone-500 mt-0.5">
                  {plan.carrier}
                  {plan.networkType && ` · ${plan.networkType}`}
                  {plan.metalTier && ` · ${plan.metalTier}`}
                </div>
                {plan.ineligibleReason ? (
                  <div className="text-[11px] text-red-700 dark:text-red-300 mt-1">{plan.ineligibleReason}</div>
                ) : lockedByRequired ? (
                  <div className="text-[11px] text-stone-500 mt-1">Enrollment in {requiredPlan.name} is required.</div>
                ) : null}
                {prem?.benefitAmount != null && (
                  <div className="text-[11px] text-stone-500 font-mono mt-1">
                    {formatBenefitAmount(prem.benefitAmount, prem.benefitAmountPeriod)}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                {planIsFsa ? (
                  // FSA cost = the employee's annual election, set in the field below — not a plan premium.
                  <div className="text-[11px] text-stone-500 max-w-[110px]">
                    Cost set by annual election below
                  </div>
                ) : cantPrice ? (
                  <div
                    className="flex items-start gap-1 max-w-[240px] text-left text-[11px] text-amber-700 dark:text-amber-400"
                    data-testid="plan-option-price-unavailable"
                    title={cantPrice}
                  >
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span className="break-words">{cantPrice}</span>
                  </div>
                ) : (
                  <>
                    <div className="text-base font-bold font-mono tracking-tight text-stone-900 dark:text-stone-100">
                      {isLoading ? "—" : formatUSD(perCycle)}
                    </div>
                    <div className="text-[10.5px] text-stone-500">{cycleLabel}</div>
                    {!isLoading && !isCurrent && Math.abs(delta) > 0.005 && (
                      <div
                        className={cn(
                          "mt-0.5 text-[10.5px] font-mono font-semibold inline-flex items-center gap-1",
                          delta > 0 ? "text-amber-700" : "text-emerald-700",
                        )}
                      >
                        {delta > 0 ? "▲" : "▼"} {formatUSD(Math.abs(delta))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </button>
        );
      })}
      {/* Waive option, dashed last row */}
      {waiveBlocked ? (
        <div className="rounded-lg border-2 border-dashed border-stone-200 dark:border-stone-700 px-3.5 py-3 text-[11.5px] text-stone-500">
          Enrollment is required for this plan, so it can't be waived.
        </div>
      ) : (
      <button
        type="button"
        onClick={() => onSelect("__waive__")}
        className={cn(
          "w-full text-left rounded-lg border-2 border-dashed bg-stone-50 dark:bg-stone-900/40 px-3.5 py-3 transition-all",
          selectedId === "__waive__"
            ? "border-stone-900 dark:border-stone-100 ring-2 ring-stone-200 dark:ring-stone-700"
            : "border-stone-300 dark:border-stone-700 hover:border-stone-500",
        )}
      >
        <div className="flex items-start gap-3">
          <span
            className={cn(
              "h-4 w-4 rounded-full border mt-0.5 shrink-0 flex items-center justify-center",
              selectedId === "__waive__"
                ? "bg-stone-900 dark:bg-stone-100 border-stone-900 dark:border-stone-100"
                : "border-stone-300 dark:border-stone-600",
            )}
          >
            {selectedId === "__waive__" && <Check className="h-2.5 w-2.5 text-white dark:text-stone-900" />}
          </span>
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-stone-900 dark:text-stone-100">
              Waive this coverage
            </div>
            <div className="text-[11.5px] text-stone-500 mt-0.5">
              Decline this benefit for the plan year. A reason is required.
            </div>
          </div>
        </div>
      </button>
      )}
    </div>
  );
}

function FsaElectionBody({
  annualElection,
  onChange,
  fsaConfig,
  payCycle,
  payScheduleReady,
}: {
  annualElection: string;
  onChange: (v: string) => void;
  fsaConfig: {
    annualElectionMin?: string | null;
    annualElectionMax?: string | null;
    employerContribution?: string | null;
    carryoverAllowed?: boolean;
    carryoverMax?: string | null;
  } | null;
  payCycle?: string | null;
  payScheduleReady?: boolean;
}) {
  const preview = computeFsaPreview(annualElection, fsaConfig, payCycle);
  const min = fsaConfig?.annualElectionMin != null ? Number(fsaConfig.annualElectionMin) : null;
  const max = preview.annualCap;
  // No usable max → the election can't be bounded; block entry until the plan is configured.
  const notConfigured = !(max != null && max > 0);

  if (notConfigured) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 px-3 py-2 flex items-start gap-2 text-xs text-amber-900 dark:text-amber-100">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            This FSA plan has no annual election limit configured. Set the maximum
            election in the plan&apos;s "FSA Config" tab before enrolling — otherwise
            the election can&apos;t be capped.
          </span>
        </div>
      </div>
    );
  }

  if (payScheduleReady === false) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 px-3 py-2 flex items-start gap-2 text-xs text-amber-900 dark:text-amber-100">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{FSA_NO_PAY_SCHEDULE_ADMIN}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800/60 px-3 py-2 flex items-start gap-2 text-xs text-blue-900 dark:text-blue-100">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          FSAs are employee-only accounts (IRS rule). Dependents' eligible
          expenses can be reimbursed without separate enrollment.
        </span>
      </div>
      <div>
        <Label className="text-[11px] uppercase font-semibold tracking-wide text-stone-500">
          Annual election ($) <span className="text-amber-700">*</span>
        </Label>
        <Input
          type="number"
          inputMode="decimal"
          min={min ?? undefined}
          max={max ?? undefined}
          step="1"
          value={annualElection}
          onChange={(e) => onChange(e.target.value)}
          placeholder={min != null && max != null ? `Between $${min} and $${max}` : "Enter annual amount"}
          className="mt-1"
        />
        {(min != null || max != null) && (
          <div className="text-[10.5px] text-stone-500 mt-1">
            {min != null && max != null
              ? `Plan range: $${min.toLocaleString()} – $${max.toLocaleString()}`
              : min != null
                ? `Plan minimum: $${min.toLocaleString()}`
                : `Plan maximum: $${max!.toLocaleString()}`}
            {fsaConfig?.carryoverAllowed && fsaConfig?.carryoverMax && (
              <> · Carryover allowed up to ${Number(fsaConfig.carryoverMax).toLocaleString()}</>
            )}
            {preview.employerContribution > 0 && (
              <> · Employer contributes ${preview.employerContribution.toLocaleString()}/year</>
            )}
          </div>
        )}
      </div>
      {preview.employeeAnnualIsValid && (
        <div className="rounded-md border border-stone-200 bg-stone-50 dark:bg-stone-900/40 dark:border-stone-700 px-3 py-2 text-xs">
          <div className="font-mono font-semibold text-stone-900 dark:text-stone-100">
            {formatUSD(preview.perPaycheckEmployeeContribution)}{" "}
            <span className="text-stone-500 font-normal">{preview.perPaycheckLabel}</span>
          </div>
          {preview.employerContribution > 0 && (
            <div className="text-stone-500 mt-0.5">
              Combined annual: ${preview.combinedAnnual.toLocaleString()} (employee + employer)
            </div>
          )}
        </div>
      )}
      {preview.overCap && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
          Combined employer (${preview.employerContribution.toLocaleString()}) + employee (${preview.employeeAnnual.toLocaleString()}) exceeds the plan max (${preview.annualCap!.toLocaleString()}).
        </div>
      )}
    </div>
  );
}

